import React from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { InputField } from '@/components/forms/input-field';
import { LoginButton } from '@/components/ui/login-button';
import { Page } from '@/components/ui/page';
import { useLanguage } from '@/contexts/language-context';
import { usePasswordForm } from '@/hooks/forms/use-password-form';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import { LoginCard } from '../login-card';

interface PasswordFactorProps {
  submitPassword: UseMfaOrchestratorReturn['submitResponse'];
  email?: string; // Email to display (for escalation flows where user is known)
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const PasswordFactor: React.FC<PasswordFactorProps> = ({
  submitPassword,
  email,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();

  const { form, onSubmit, handleKeyPress, isSubmitting, getErrorMessage } =
    usePasswordForm({ submitPassword });

  const { register, handleSubmit } = form;

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-1'>
            <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
              {t('auth.loginToYourAccount')}
            </Text>
            {!username && email ? (
              <Text as='span' size={TextSizes.MD}>
                {t('auth.enterPasswordFor') || 'Enter your password for'}{' '}
                <Text
                  as='span'
                  size={TextSizes.MD}
                  weight={FontWeights.SEMIBOLD}
                >
                  {email}
                </Text>
              </Text>
            ) : (
              <Text as='span' size={TextSizes.MD}>
                {t('auth.enterPassword')}
              </Text>
            )}
          </div>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='flex flex-col gap-4'>
              {/* Hidden username field so password managers can associate the
                  saved password with the username from the previous step. */}
              <input
                type='text'
                name='username'
                value={username ?? email ?? ''}
                autoComplete='username'
                readOnly
                hidden
                aria-hidden='true'
                tabIndex={-1}
                data-testid='hidden-username-input'
              />
              <InputField
                autoFocus
                data-testid='password-input'
                placeholder={t('auth.password')}
                inputType='password'
                autoComplete='current-password'
                {...register('password')}
                onKeyPress={handleKeyPress}
                error={getErrorMessage()}
              />

              <LoginButton
                data-testid='submit-password-button'
                type='submit'
                size={Sizes.LG}
                className='w-full'
                isLoading={isSubmitting}
              >
                {isSubmitting ? t('auth.signingIn') : t('common.verify')}
              </LoginButton>
            </div>
          </form>
        </div>
      </LoginCard>
    </Page>
  );
};
